/**
 * Phase 5 batch 3 Slice 2 — small "you are signed in" pill that lives
 * in the top bar. Shows the user's email and exposes a sign-out button.
 */
import { useState } from 'react';
import type { AuthClient } from '@inside-us-aos/firebase-adapter';
import type { User } from 'firebase/auth';

import styles from './AuthGate.module.css';

export interface ISignedInBadgeProps {
  authClient: AuthClient;
  user: User;
}

export function SignedInBadge({
  authClient,
  user,
}: ISignedInBadgeProps): JSX.Element {
  const [busy, setBusy] = useState(false);

  const handleSignOut = async (): Promise<void> => {
    setBusy(true);
    try {
      await authClient.signOut();
    } finally {
      // The auth listener fires before this resolves and unmounts the
      // intake; setBusy(false) is defensive in case sign-out errored
      // and we're still on screen.
      setBusy(false);
    }
  };

  const display = user.email ?? user.displayName ?? user.uid;

  return (
    <span className={styles.badge} aria-label="Signed-in user">
      <span className={styles.badge__email} title={display}>
        {display}
      </span>
      <button
        type="button"
        className={styles.badge__signout}
        onClick={() => {
          void handleSignOut();
        }}
        disabled={busy}
      >
        Sign out
      </button>
    </span>
  );
}
